import { useEffect, useState } from 'react';
import { Plus, Copy, Download, Loader2 } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function QrCodesSettingsPage() {
  const { hasPermission } = useAuth();
  const canManageSettings = hasPermission('all');
  const shortUrlBase = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api/s/`
    : `${window.location.origin}/api/s/`;

  const [qrCodes, setQrCodes] = useState([]);
  const [qrCodeLoading, setQrCodeLoading] = useState(true);
  const [qrCodeSaving, setQrCodeSaving] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const [qrCodeForm, setQrCodeForm] = useState({
    name: '',
    original_url: '',
    code: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    is_active: true
  });

  const resetQrCodeForm = () => {
    setQrCodeForm({
      name: '',
      original_url: '',
      code: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_content: '',
      utm_term: '',
      is_active: true
    });
  };

  const fetchQrCodes = async () => {
    setQrCodeLoading(true);
    try {
      const response = await adminApi.getQrCodes();
      setQrCodes(response.data?.items || []);
    } catch (error) {
      toast.error('Failed to load QR codes');
    } finally {
      setQrCodeLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageSettings) return;
    fetchQrCodes();
  }, [canManageSettings]);

  const handleSaveQrCode = async () => {
    if (!qrCodeForm.original_url.trim()) {
      toast.error('Original URL is required');
      return;
    }
    setQrCodeSaving(true);
    try {
      await adminApi.createQrCode({
        name: qrCodeForm.name || undefined,
        original_url: qrCodeForm.original_url,
        code: qrCodeForm.code || undefined,
        utm_source: qrCodeForm.utm_source || undefined,
        utm_medium: qrCodeForm.utm_medium || undefined,
        utm_campaign: qrCodeForm.utm_campaign || undefined,
        utm_content: qrCodeForm.utm_content || undefined,
        utm_term: qrCodeForm.utm_term || undefined,
        is_active: qrCodeForm.is_active
      });
      toast.success('QR code created');
      setShowQrCodeModal(false);
      resetQrCodeForm();
      fetchQrCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create QR code');
    } finally {
      setQrCodeSaving(false);
    }
  };

  const handleCopyQrLink = async (code, qrId) => {
    try {
      await navigator.clipboard.writeText(`${shortUrlBase}${code}?qr=${qrId}`);
      toast.success('QR link copied');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const handleDownloadQr = (qrId, name) => {
    const canvas = document.getElementById(`qr-canvas-${qrId}`);
    if (!canvas) {
      toast.error('QR code not ready');
      return;
    }
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${name || 'qr-code'}.png`;
    link.click();
  };

  if (!canManageSettings) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">QR Codes</h1>
        <p className="text-[#A8A8A8] mt-1">Generate QR codes tied to short URLs for scan analytics</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg text-[#2D2420]">QR Codes</CardTitle>
            <CardDescription>Generate QR codes tied to short URLs for scan analytics</CardDescription>
          </div>
          <Button
            onClick={() => { resetQrCodeForm(); setShowQrCodeModal(true); }}
            className="bg-[#C97A5E] hover:bg-[#B06850]"
          >
            <Plus className="w-4 h-4 mr-2" /> Create QR Code
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {qrCodeLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : qrCodes.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              No QR codes yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">QR</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Original URL</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Short URL</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Scans</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {qrCodes.map((item) => {
                    const qrLink = `${shortUrlBase}${item.code}?qr=${item.id}`;
                    return (
                      <tr key={item.id} className="hover:bg-[#FAFAF9]/50">
                        <td className="py-3 px-4">
                          <QRCodeCanvas
                            id={`qr-canvas-${item.id}`}
                            value={qrLink}
                            size={256}
                            style={{ width: 48, height: 48 }}
                          />
                        </td>
                        <td className="py-3 px-4 text-[#2D2420] font-medium">{item.name || '-'}</td>
                        <td className="py-3 px-4 text-[#2D2420] max-w-xs truncate" title={item.original_url}>
                          {item.original_url}
                        </td>
                        <td className="py-3 px-4 text-[#2D2420]">
                          <a
                            href={qrLink}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#C97A5E] hover:underline"
                          >
                            {qrLink}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-right text-[#2D2420]">{item.total_scans || 0}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleCopyQrLink(item.code, item.id)}
                              className="text-[#5C4033] hover:bg-[#FAFAF9]"
                              title="Copy"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadQr(item.id, item.name)}
                              className="text-[#5C4033] hover:bg-[#FAFAF9]"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showQrCodeModal} onOpenChange={setShowQrCodeModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Create QR Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Name</Label>
              <Input
                value={qrCodeForm.name}
                onChange={(e) => setQrCodeForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Optional label"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Original URL *</Label>
              <Input
                value={qrCodeForm.original_url}
                onChange={(e) => setQrCodeForm((prev) => ({ ...prev, original_url: e.target.value }))}
                placeholder="https://example.com"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Custom Code</Label>
              <Input
                value={qrCodeForm.code}
                onChange={(e) => setQrCodeForm((prev) => ({ ...prev, code: e.target.value }))}
                placeholder="Optional short code"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Source</Label>
                <Input
                  value={qrCodeForm.utm_source}
                  onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_source: e.target.value }))}
                  placeholder="utm_source"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Medium</Label>
                <Input
                  value={qrCodeForm.utm_medium}
                  onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_medium: e.target.value }))}
                  placeholder="utm_medium"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Campaign</Label>
                <Input
                  value={qrCodeForm.utm_campaign}
                  onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_campaign: e.target.value }))}
                  placeholder="utm_campaign"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Content</Label>
                <Input
                  value={qrCodeForm.utm_content}
                  onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_content: e.target.value }))}
                  placeholder="utm_content"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">UTM Term</Label>
                <Input
                  value={qrCodeForm.utm_term}
                  onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_term: e.target.value }))}
                  placeholder="utm_term"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[#5C4033]">Active</Label>
                  <p className="text-sm text-[#A8A8A8]">Enable QR code</p>
                </div>
                <Switch
                  checked={qrCodeForm.is_active}
                  onCheckedChange={(checked) => setQrCodeForm((prev) => ({ ...prev, is_active: checked }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleSaveQrCode}
              disabled={qrCodeSaving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {qrCodeSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
